/*============================================================================*/

/**
 * Getter functions
 *
 * Functions for retrieving data from the DOM, datasets, or
 * environment variables. They do not modify state.
 */

/*============================================================================*/


/**
 * Fetch the content of an SVG file from the assets folder
 *
 * @param {Object} args - Configuration object
 * @param {string} args.fileName - Name of the SVG file (without extension)
 * @returns {Promise<string|null>} The SVG content as a string, or null if not found
 */
function getSVGContent({ fileName })
{
    const svgPath = `${THEME_URL}assets/svg/${fileName}.svg`;

    return fetch(svgPath)
        .then(response => 
        {
            if (response.ok)
            {
                return response.text();
            }
            else
            {
                console.error(`SVG not found: ${svgPath}`);
                return null;
            }
        })
        .catch(error => 
        {
            console.error('Error fetching SVG:', error);
            return null;
        });
}

/**
 * Retrieves the value of a CSS custom property (variable) from the :root element.
 *
 * Useful for grid sizing or other CSS variables defined in the document.
 *
 * @param {Object} args - Function arguments
 * @param {string} args.varName - Name of the CSS variable (with or without -- prefix)
 * @returns {string|null} The trimmed value of the CSS variable, or null if not set
 */
function getCssVarValue({ varName })
{
    if (!varName.startsWith('--'))
    {
        varName = `--${varName}`;
    }

    const value = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return value.trim() || null;
}

/**
 * Retrieve image URLs defined in CSS variables
 *
 * Iterates through the given CSS variable names, reads their values,
 * and extracts any URLs (e.g., background images, SVGs) contained within.
 *
 * @param {Object} args - Function arguments
 * @param {string[]} args.cssVariables - List of CSS variable names to check
 * @returns {string[]} Array of extracted image URLs
 */
function getCssVariableImageUrls({ cssVariables = [] })
{
    const rootStyles = getComputedStyle(document.documentElement);
    const urls = [];

    cssVariables.forEach((variable) =>
    {
        const value = rootStyles.getPropertyValue(variable).trim();
        const match = value.match(/url\(['"]?(.*?)['"]?\)/);

        if (match && match[1])
        {
            const url = match[1];
            urls.push(url);
        }
    });

    return urls;
}

/**
 * Retrieve the value of a query parameter from the current URL
 *
 * Useful for extracting any query values.
 *
 * @param {Object} args - Function arguments
 * @param {string} args.param - The query parameter name to retrieve
 * @returns {string} The decoded parameter value, or an empty string if not found
 */
function getUrlParameterValue({ param })
{
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(param) ? decodeURIComponent(searchParams.get(param)) : '';
}

/**
 * Serialize form data
 *
 * Collects all form fields and serializes their values into a query string.
 * This includes native form inputs as well as custom components
 * like `.compDropdown` and `.compCheckable`.
 *
 * @param {Object} args - Configuration object
 * @param {string} args.formId - The ID of the form to serialize
 * @param {string} [args.selectValueFormat="both"] - How to include dropdowns:
 *  - "value" → only the value
 *  - "label" → only the label
 *  - "both" → combine "value - label"
 * @returns {string} Serialized form data as query string
 */
function getSerializedFormData({ formId, selectValueFormat = "both" })
{
    const form = jQuery(`#${formId}`)
    let formData = form.serialize()

    form.find('.compDropdown').each(function ()
    {
        const selectElement = jQuery(this)
        const selectValue = selectElement.attr('value') || 'unset'
        const selectedItem = selectElement.find(`.compDropdownItem[value="${selectValue}"]`)
        const selectLabel = selectedItem.attr('label') || 'undefined'
        const fieldName = selectElement.attr('name') || selectElement.attr('id')

        let valueToAppend = ""

        if (selectValueFormat === "value")
        {
            valueToAppend = selectValue
        }
        else if (selectValueFormat === "label")
        {
            valueToAppend = selectLabel
        }
        else
        {
            valueToAppend = `${selectValue} - ${selectLabel}`
        }

        formData += `&${fieldName}=${valueToAppend}`
    })

    form.find('.compCheckable').each(function ()
    {
        const checkElement = jQuery(this)
        const checkValue = checkElement.attr('status') || 'false'
        const fieldName = checkElement.attr('name') || checkElement.attr('id')
    
        formData += `&${encodeURIComponent(fieldName)}=${encodeURIComponent(checkValue)}`
    })

    form.find('.compCheckableMultipleOptions').each(function()
    {
        const multiElement = jQuery(this)
        let selectedOptions = []
        multiElement.find('.checkableOption').each(function()
        {
            const opt = jQuery(this)
            if (opt.attr('status') === 'true')
            {
                selectedOptions.push(opt.attr('value'))
            }
        })
        const fieldName = multiElement.attr('name') || multiElement.attr('id')
        if (selectedOptions.length)
        {
            formData += `&${encodeURIComponent(fieldName)}=${encodeURIComponent(selectedOptions.join(','))}`
        }
    })

    return formData
}

/**
 * Get the raw date from an element's `data-date` attribute.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.selector - Selector or element to read the date from.
 * @returns {string|null} The raw date string, or null if not present.
 */
function getDate({ selector } = {})
{
    const element = jQuery(selector);
    const rawDate = element.data("date");
    return !isEmpty(rawDate) ? rawDate : null;
}

/**
 * Returns the visible height (in pixels) of any element in the viewport.
 * Works for Upper Menu, Lower Menu, or any other element.
 *
 * @param {Object} options
 * @param {string|Element|jQuery} options.element - The selector or element to measure.
 * @param {number} [options.maxWindowHeight=window.innerHeight] - Optional custom viewport height.
 * @returns {number} visible height in pixels
 */
function getVisibleMenuHeight({ element, maxWindowHeight = window.innerHeight } = {})
{
    const menu = jQuery(element);
    if (!menu.length) return 0;

    const rect = menu[0].getBoundingClientRect();
    const visibleHeight = Math.max(0, Math.min(rect.bottom, maxWindowHeight) - Math.max(rect.top, 0));

    return Math.round(visibleHeight);
}

/**
 * Returns the outer height of an element if it is currently visible and not hidden.
 * @param {string} selector
 * @returns {number}
 */
function getVisibleElementHeight(selector)
{
    const el = jQuery(selector);
    if (el.length && el.is(':visible') && !el.hasClass('hidden'))
    {
        return el.outerHeight() || 0;
    }
    return 0;
}

/**
 * Returns the full outer height of an element regardless of scroll hiding state.
 * @param {string} selector
 * @returns {number}
 */
function getFullElementHeight(selector)
{
    const el = jQuery(selector);
    if (el.length && !el.hasClass('loading') && el.css('display') !== 'none')
    {
        return el.outerHeight() || 0;
    }
    return 0;
}